import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import _ from 'lodash';
import cn from 'classnames';
import './HomeShopFeatures.scss';

import { openNewCircleModal } from '../../../Actions/UIActions';
import { syncShopFeatures } from '../../../Actions/FeaturedActions';

import { getShopFeaturedResultsForType } from '../../../Helpers/featured_helpers';

import ShopTile from '../../Shop/ShopTile';

const HomeShopFeatures = props => {
  // Core Data
  const data =
    {
      curators: {
        header: 'Curator',
        subheader: 'Insider access to the products loved by the world’s most trusted tastemakers.',
        showcaseLargeFeature: true,
        numFeatures: 5,
        showAllLink: '/shop/discover/curators',
        actions: [
          {
            display: 'Shop All Curators',
            link: '/shop/discover/curators',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'curators')
      },
      groups: {
        header: 'Circle',
        subheader: 'Shop groups of recommendations or create your own circle to design a shop built just for you.',
        numFeatures: 4,
        showAllLink: '/shop/discover/circles',
        actions: [
          {
            display: 'Create Circle',
            onClick: () => props.openNewCircleModal({ source: 'Home Featured Circles' })
          },
          {
            display: 'Shop All Circles',
            link: '/shop/discover/circles',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'circles')
      },
      catalog: {
        header: 'Category',
        subheader: 'Shop with confidence through the combined advice of thousands of tastemakers.',
        numFeatures: 8,
        showAllLink: '/shop/discover/categories',
        actions: [
          {
            display: 'Shop All Categories',
            link: '/shop/discover/categories',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'catalog')
      },
      brands: {
        header: 'Brand',
        numFeatures: 4,
        subheader: "Explore the most highly sought after products from brands you know and those you're just discovering.",
        showAllLink: '/shop/discover/brands',
        actions: [
          {
            display: 'Shop All Brands',
            link: '/shop/discover/brands',
            className: 'primary'
          }
        ],
        results: getShopFeaturedResultsForType(props.featured, 'brands')
      }
    }[props.type] || {};
  const { header, subheader, actions, results, showcaseLargeFeature, numFeatures } = data;

  // UI handling
  const additionalClasses = { 'showcase-large-feature': showcaseLargeFeature };

  return (
    <div ref={props.containerRef} className='home-shop-features-outer-container'>
      <div className='home-shop-features-inner-container'>
        <div className='header-section'>
          <div className='main'>
            <div className='shop-by'>shop by</div>
            <div className='header'>{header}</div>
          </div>
          <div className='secondary'>
            <div className='subheader'>{subheader}</div>
            {!!actions?.length && (
              <div className='actions'>
                {actions.map(action => {
                  const { link, onClick, display, className } = action;
                  return link ? (
                    <Link key={display} to={link} className={cn('action', className)}>
                      {display}
                    </Link>
                  ) : (
                    <div onClick={onClick} key={display} className={cn('action', className)}>
                      {display}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className={cn('features-section', additionalClasses)}>
          {showcaseLargeFeature && (
            <div className={cn('features-subsection large-feature', { loading: !results?.[0] })}>
              <div className='feature'>
                {results?.[0] && <ShopTile featured={props.featured} feature={results?.[0]} syncShopFeatures={props.syncShopFeatures} />}
              </div>
            </div>
          )}
          <div className={cn('features-subsection grid', additionalClasses)}>
            {_.range(numFeatures).map(i => {
              const feature = results?.[i];
              return (
                <div key={i} className={cn('feature', { loading: !feature })}>
                  {feature && <ShopTile featured={props.featured} feature={feature} syncShopFeatures={props.syncShopFeatures} />}
                </div>
              );
            })}
          </div>
        </div>
        {actions.length && (
          <div className='actions-mobile'>
            {actions.map(action => {
              const { link, onClick, display, className } = action;
              return link ? (
                <Link key={display} to={link} className={cn('action', className)}>
                  {display}
                </Link>
              ) : (
                <div onClick={onClick} key={display} className={cn('action', className)}>
                  {display}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

HomeShopFeatures.propTypes = {
  // From Outside
  type: PropTypes.oneOf(['curators', 'groups', 'brands', 'catalog']).isRequired,
  containerRef: PropTypes.object, // If needed to scroll to this component

  // From Inside
  featured: PropTypes.object.isRequired,
  syncShopFeatures: PropTypes.func.isRequired,
  openNewCircleModal: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { featured } = state;
  return { featured };
};

export default connect(mapStateToProps, {
  syncShopFeatures,
  openNewCircleModal
})(HomeShopFeatures);
